import { useStripe } from "@stripe/react-stripe-js";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO } from "date-fns";

import { useToast } from "../../components/toast";
import { OrderableType, PricingType } from "../../data";
import { useGetPartnerOffering } from "../../data/api";
import {
  buildPathWithParams,
  useQueryString,
} from "../../hooks/use_query_string";
import { useI18n } from "../../lib/i18n/i18n_provider";
import {
  useTrackCheckoutStartedEcommerceEventOnMount,
  useTrackOrderCompletedEcommerceEvent,
  useTrackProductViewedEcommerceEvent,
} from "../../offering/analytics_ecommerce_events";
import {
  Order,
  QuoteInput,
  useLocationQuery,
  useSpaceBookingPolicyQuery,
  useSpaceOfferingQuery,
  useSpaceOfferingsQuery,
  useSpaceQuery,
} from "../../offering/api";
import {
  normalizeDailyAccessOrderParams,
  toQuoteInput,
  useDailyAccessOrderParamsHandlers,
  useSpaceAvailableDaysQuery,
} from "../../offering/order_params";
import { PartnerConfig } from "../../offering/partner_config";
import { useCheckoutStripe } from "../../offering/template_offering_checkout_stripe_base";
import { QuoteViewDailyAccessDesktop } from "../components/quote_view";
import { TemplateOfferingCheckoutBaseV2 } from "./template_offering_checkout_base";
import {
  useAppleGooglePayEmbedInCheckoutPage,
  useEnhanceCheckoutUX,
} from "../../core/splitio";
import { usePrevious } from "../../hooks/use_previous";
import { usePassedDateRedirect } from "../../hooks/use_passed_date_redirect";
import { CreditOption } from "../components/credit_quantity_picker";
import { useQuoteQuery } from "@/offering/hooks/use_quote_query";
import { getPartnerOfferingName } from "@/offering/get_partner_offering_name";
import {
  getStartDateFromOrderParams,
  getEndDateFromOrderParams,
} from "@/offering/order_params_utils";

interface TemplateOfferingCheckoutPathParams {
  offeringID: string;
}

interface TemplateOfferingCheckoutQueryParams {
  spaceID?: string;
  startDate?: string;
  endDate?: string;
  addOnID?: string;
  spaceOfferingID?: string;
  fromCatalog?: string;
}

export function TemplateOfferingCheckoutStripeDailyAccessV2(props: {
  partnerConfig: PartnerConfig;
  showSinglePass?: boolean;
}) {
  const { partnerConfig, showSinglePass = true } = props;
  const {
    firstDayOfWeek,
    defaultMarketingConsent,
    includeQuantity = true,
  } = partnerConfig;
  const pageParams = useParams<TemplateOfferingCheckoutPathParams>();
  const partnerOfferingID = pageParams.offeringID;
  const partnerOffering = useGetPartnerOffering(partnerOfferingID);
  const isGoogleApplePayEmbedInCheckoutPage =
    useAppleGooglePayEmbedInCheckoutPage(partnerOffering.partner.id);
  const isEnhanceCheckoutUXFlagEnabled = useEnhanceCheckoutUX();
  const queryParams = useQueryString<TemplateOfferingCheckoutQueryParams>();
  const { fbt } = useI18n();
  const toast = useToast();
  const [order, setOrder] = useState<Order>();

  let additionalDayPassAddOn = useMemo(
    () => ({
      id: "daypass",
      name: fbt("Single pass"),
      displayName: fbt("Single pass"),
      description: "",
      daysToExpire: 0,
      productCategory: "",
      productTypeName: "",
      numberOfEntries: 1,
      price: 0,
      spaceID: "",
      addOnID: "daypass",
    }),
    [fbt]
  );
  const whitelistedAddOns = useMemo(() => {
    const cloned = partnerOffering.addOns.slice();
    cloned.unshift(additionalDayPassAddOn);
    return cloned;
  }, [additionalDayPassAddOn, partnerOffering.addOns]);
  const {
    orderParams,
    orderParamsError,
    validateOrderParams,
    handleChangeAddOnID,
    handleChangeDateRange,
    handleChangeQuantity,
    handleChangeLocation,
    handleChangePromoCode,
    handleChangeEmail,
    handleChangeCity,
    setOrderParams,
  } = useDailyAccessOrderParamsHandlers(
    whitelistedAddOns,
    normalizeDailyAccessOrderParams(queryParams, includeQuantity)
  );

  usePassedDateRedirect({
    startDate: orderParams.dateRange?.startDate,
    endDate: orderParams.dateRange?.endDate,
    partnerOfferingID,
  });
  const stripe = useStripe();
  const selectedSpaceQuery = useSpaceQuery(orderParams.spaceID);
  const { data: space } = selectedSpaceQuery;
  const selectedLocationQuery = useLocationQuery(space?.locationID);
  const { data: location } = selectedLocationQuery;
  const selectedSpaceID = space?.id;
  const { data: spaceOfferings, loading: spaceOfferingLoading } =
    useSpaceOfferingsQuery(
      selectedSpaceID,
      partnerOffering.addOns.map((addOn) => addOn.id)
    );
  const multiPasses = useMemo(() => {
    const isCreditPackage = partnerOffering.addOns.find((aO) => !!aO.credits);
    if (isCreditPackage) {
      return [];
    }

    const cloned = spaceOfferings?.slice() || [];
    const dailyPricing = space?.pricings.find(
      (p) => p.type === PricingType.Daily
    );
    additionalDayPassAddOn.price = dailyPricing?.price || 0;

    if (showSinglePass && !spaceOfferingLoading) {
      cloned.unshift(additionalDayPassAddOn);
    }
    return cloned;
  }, [
    additionalDayPassAddOn,
    partnerOffering.addOns,
    showSinglePass,
    space?.pricings,
    spaceOfferingLoading,
    spaceOfferings,
  ]);
  const credits = useMemo(() => {
    const isNotCreditPackage = partnerOffering.addOns.find(
      (aO) => aO.credits === null || aO === undefined
    );
    if (isNotCreditPackage) {
      return [];
    }

    const credits = partnerOffering.addOns.reduce<CreditOption[]>(
      (accumulator, addOn) => {
        const offering = spaceOfferings?.find(
          (sO: any) => sO.addOnID === addOn.id
        );
        const { displayName, price, id: offeringId } = offering || {};
        if (displayName && price && offeringId && addOn.credits) {
          return [
            ...accumulator,
            {
              id: addOn.id,
              offeringId,
              displayName,
              numberOfCredits: addOn.credits,
              price: Number(price),
            },
          ];
        }
        return accumulator;
      },
      []
    );

    return credits;
  }, [partnerOffering.addOns, spaceOfferings]);
  const {
    isOutsideRange,
    isBlockedDate,
    handleNextVisibleDates,
    availableDays,
    config: availabilityConfig,
  } = useSpaceAvailableDaysQuery(selectedSpaceID, orderParams);
  const [isUpdateQuote, setIsUpdateQuote] = useState<boolean>(true);
  const [spaceLayoutID, setSpaceLayoutID] = useState<string>();
  useEffect(() => {
    if (space) setSpaceLayoutID(space.defaultLayoutID);
  }, [space, setSpaceLayoutID]);

  const quoteInput = useMemo(
    () =>
      selectedSpaceID && spaceLayoutID
        ? toQuoteInput(
            selectedSpaceID,
            partnerOffering.partner.id,
            orderParams,
            "WL",
            {
              layoutID: spaceLayoutID,
              attendees: 1,
            }
          )
        : null,
    [selectedSpaceID, partnerOffering.partner.id, orderParams, spaceLayoutID]
  );
  const [quoteParams, setQuoteParams] = useState<QuoteInput | null>();
  useEffect(() => {
    if (isUpdateQuote && selectedSpaceID) {
      setQuoteParams(quoteInput);
    }
  }, [quoteInput, setQuoteParams, isUpdateQuote, selectedSpaceID]);

  const {
    data: quote,
    error: quoteError,
    loading: quoteLoading,
  } = useQuoteQuery(quoteParams);

  const offeringName =
    space?.productType && location
      ? fbt(
          fbt.param("productTypeName", space.productType?.name) +
            " to " +
            fbt.param("locationName", location.name)
        )
      : "";
  const trackProductViewed = useTrackProductViewedEcommerceEvent();
  const prevSpace = usePrevious(space);
  const selectedSpaceOffering = partnerOffering.addOns.find(
    (po) => po.addOnID === orderParams.addOnID
  );
  const prevSpaceOffering = usePrevious(selectedSpaceOffering);
  useEffect(() => {
    if (
      quote &&
      space &&
      (prevSpace?.id !== space.id ||
        prevSpaceOffering?.id !== selectedSpaceOffering?.id)
    ) {
      // We want to track item viewed event when space is changed on checkout page
      // so that "Product Viewed" is always greater than "Checkout Started" in funnel
      // and reflect the fact that they are viewing a new item
      trackProductViewed(
        getPartnerOfferingName(partnerOffering),
        space,
        location,
        quote,
        selectedSpaceOffering,
        partnerOffering
      );
    }
  }, [
    space,
    partnerOffering,
    prevSpace,
    quote,
    trackProductViewed,
    location,
    selectedSpaceOffering,
    prevSpaceOffering,
  ]);
  useTrackCheckoutStartedEcommerceEventOnMount(
    getPartnerOfferingName(partnerOffering),
    quote,
    space,
    location,
    selectedSpaceOffering,
    partnerOffering
  );
  const trackOrderCompletedEcommerceEvent =
    useTrackOrderCompletedEcommerceEvent(partnerOffering);

  const { data: spaceBookingPolicy } = useSpaceBookingPolicyQuery(
    space?.id,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );
  const askForBookingReason = !!spaceBookingPolicy?.askForBookingReason;

  const quotePositivePrice = quote ? quote.totalPrice > 0 : false;
  const askForTax =
    (space?.bookingPolicy.askForTax && quotePositivePrice) || false;
  const requireTaxId = spaceBookingPolicy?.requireTaxId || false;
  const addOnOrderItem = quote?.orderItems.find(
    (o) => o.type === OrderableType.AddOn
  );
  const bookingOrderItem = quote?.orderItems.find(
    (o) => o.type === OrderableType.Booking
  );
  const spaceOffering = useSpaceOfferingQuery(
    orderParams.spaceID,
    addOnOrderItem ? orderParams.addOnID : undefined
  );
  const [promotableType, setPromotableType] = useState<string>("Space");
  const [promotableID, setPromotableID] = useState<string | undefined>(
    orderParams.spaceID
  );
  useEffect(() => {
    if (addOnOrderItem && spaceOffering) {
      setPromotableType("SpaceOffering");
      setPromotableID(spaceOffering?.id);
    }
    if (bookingOrderItem) {
      setPromotableID(selectedSpaceID);
      setPromotableType("Space");
    }
  }, [addOnOrderItem, spaceOffering, selectedSpaceID, bookingOrderItem]);

  const form = useCheckoutStripe({
    stripe,
    requireBillingDetails: quotePositivePrice,
    requirePayment: quotePositivePrice,
    askForTax,
    requireTaxId,
    initialValues: {
      fullName: "",
      companyName: "",
      phoneNumber: "",
      email: orderParams.email || "",
      billingFullName: "",
      billingPhoneNumber: "",
      billingEmail: "",
      billingStreetAddress: "",
      billingCity: "",
      billingState: "",
      billingPostalCode: "",
      billingCountry: "",
      billingTax: "",
      paymentComplete: false,
      termsChecked: false,
      marketingConsent: defaultMarketingConsent || false,
      membershipNumber: "",
    },
    useStripePaymentElement: true,
    partnerID: partnerOffering.partner.id,
    quoteID: quote?.id,
    partnerOfferingID: partnerOffering.id,
    legacy__details: {
      layoutID: spaceLayoutID || "",
      attendees: 1,
    },
    orderParams,
    onComplete: (order: Order) => {
      trackOrderCompletedEcommerceEvent(
        getPartnerOfferingName(partnerOffering),
        order,
        space,
        location,
        selectedSpaceOffering
      );

      const pathWithParams = buildPathWithParams(
        `/offerings/confirm_order/${partnerOffering.slug}/${order.id}`
      );
      history.push(pathWithParams);
    },
    onError: (errorMessage: string) => toast.error({ message: errorMessage }),
    onOrderCreated: (createdOrder) => setOrder(createdOrder),
  });

  const history = useHistory();
  const disabledCheckout = !!(
    (!form.values.paymentComplete &&
      !isEnhanceCheckoutUXFlagEnabled &&
      isGoogleApplePayEmbedInCheckoutPage &&
      quotePositivePrice) ||
    quoteLoading ||
    quoteError ||
    orderParamsError.dateRange ||
    orderParamsError.spaceID
  );

  return (
    <TemplateOfferingCheckoutBaseV2
      disabledCheckout={disabledCheckout}
      includeQuantity={includeQuantity}
      analyticsName={getPartnerOfferingName(partnerOffering)}
      offeringName={offeringName}
      askForBookingReason={askForBookingReason}
      partnerOfferingSlug={partnerOfferingID}
      partnerConfig={partnerConfig}
      location={location}
      space={space}
      spaceOffering={selectedSpaceOffering}
      partnerOffering={partnerOffering}
      askForTax={askForTax}
      requireTaxId={requireTaxId}
      orderParams={orderParams}
      validateOrderParams={validateOrderParams}
      onChangeEmail={handleChangeEmail}
      quote={quote}
      order={order}
      onPaymentClose={() => setOrder(undefined)}
      onPressBack={() => {
        if (history.length > 1) {
          history.goBack();
        } else {
          const pathWithParams = buildPathWithParams(
            `/offerings/${partnerOfferingID}`
          );
          history.replace(pathWithParams);
        }
      }}
      desktopCheckoutBox={
        <QuoteViewDailyAccessDesktop
          includeQuantity={includeQuantity}
          quoteLoading={quoteLoading}
          quoteError={quoteError}
          firstDayOfWeek={firstDayOfWeek}
          orderParams={orderParams}
          onResetOrderParams={(orderParams) => setOrderParams(orderParams)}
          orderParamsErrors={orderParamsError}
          multiPasses={multiPasses || []}
          isOutsideRange={isOutsideRange}
          isBlockedDate={isBlockedDate}
          onChangeAddOnID={handleChangeAddOnID}
          onChangeLocation={handleChangeLocation}
          onChangeDateRange={handleChangeDateRange}
          onChangeQuantity={handleChangeQuantity}
          onNextVisibleDates={handleNextVisibleDates}
          onChangeCity={handleChangeCity}
          onUpdateQuote={(isUpdate) => setIsUpdateQuote(isUpdate)}
          locations={partnerOffering.offeringLocations}
          offeringName={getPartnerOfferingName(partnerOffering)}
          datePickerPlaceholder={partnerConfig.datePickerPlaceholder}
          showDiscountLabel={partnerOffering.showDiscountLabel}
          location={location}
          quote={quote}
          space={space}
          partnerID={partnerOffering.partner.id}
          onChangePromoCode={handleChangePromoCode}
          promotableType={promotableType}
          promotableID={promotableID}
          email={orderParams.email}
          availableDays={availableDays?.map((d) => parseISO(d.date))}
          multiDayBookingAllowed={availabilityConfig.multiDayBookingAllowed}
          credits={credits}
          singleDaySelect={partnerOffering.singleDaySelect}
        />
      }
      mobileCheckoutBox={null}
      form={form}
    />
  );
}
